import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Loader2, ArrowLeft, ChevronDown, ChevronUp, Check, Flame, Dumbbell, Utensils, Timer, Plus, Minus, Play, Pause, Square, RotateCcw, X, Info, Repeat, Search, Sparkles, AlertTriangle, Lock, Satellite } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

// GPS run tracking only works inside the native app.
const isNativeApp = Capacitor.isNativePlatform();
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const L = {
  es: {
    back: 'Volver a planes', loading: 'Cargando...', week: 'Semana', deload: 'Descarga',
    rest: 'descanso', save: 'Guardar', saved: 'Guardado', saving: 'Guardando...', saveError: 'Error al guardar',
    done: 'Completada', markDone: 'Marcar como completada', notes: 'Notas de la sesión', notesPh: 'Sensaciones, cargas, etc.',
    timerLabel: 'Tiempo de sesión', timerStart: 'Iniciar', timerStop: 'Terminar', timerReset: 'Reiniciar',
    set: 'Serie', reps: 'Reps', weight: 'Peso (kg)', target: 'Objetivo', progression: 'Progresión',
    addSet: 'Añadir serie', removeSet: 'Quitar serie',
    cardio: 'Cardio', intensity: 'Intensidad', intervals: 'Intervalos', duration: 'Duración',
    restTimer: 'Descanso', mistakes: 'Errores comunes', instructions: 'Instrucciones', viewDetail: 'Ver detalle',
    noDetail: 'No hay más detalles para este ejercicio.', resume: 'Reanudar', pause: 'Pausar', reset: 'Reiniciar',
    lastTime: 'Valor de la última vez (edítalo si cambia)',
    swap: 'Cambiar', swapTitle: 'Cambiar ejercicio', swapHint: 'Pulsa un ejercicio para ver su detalle, o «Cambiar» para sustituirlo. Solo se cambia en esta sesión: se mantienen series, reps y descanso.',
    addExercise: 'Añadir ejercicio', addTitle: 'Añadir ejercicio', addHint: 'Busca y pulsa un ejercicio para añadirlo a este día (3×8-12 por defecto; luego lo ajustas).', add: 'Añadir',
    searchPh: 'Buscar ejercicio...', noResults: 'Sin resultados', current: 'Actual',
    swapToThis: 'Sustituir por este',
    suggest: 'Sugerir series',
    rmTitle: 'Tu fuerza (1RM)', rmCurrent: 'Tu 1RM', rmNone: 'Aún no has registrado tu 1RM para este ejercicio.',
    rmKnown: 'Lo sé', rmEstimate: 'Estimar desde una serie', rmSave: 'Guardar', rmCalc: 'Calcular y guardar',
    rmKg: '1RM (kg)', rmManual: 'manual', rmEstimated: 'estimado',
    rmHint: 'Se usa para proponerte los pesos con "Sugerir series".',
    rmSetWeight: 'Peso (kg)', rmSetReps: 'Repeticiones', rmExample: 'Ej: 50 kg × 8 reps → calcula tu 1RM',
    rmAddedWeight: 'Peso añadido (kg)',
    rmBodyweightHint: 'Este ejercicio es de peso corporal: pon 0 si lo haces solo con tu cuerpo, o los kg del lastre (chaleco, mochila, disco). Yo calculo la carga real a partir de tu peso corporal.',
    cardioHelpTitle: '¿Qué significa esto?',
    cardioHelpRpe: 'RPE = Esfuerzo Percibido',
    cardioHelpRpeDesc: 'Una escala del 1 al 10 de lo duro que TÚ sientes el esfuerzo (sin pulsómetro). 1 = parado, 10 = máximo.',
    cardioHelpRpe6: 'RPE 6 — moderado: puedes hablar frases cortas.',
    cardioHelpRpe7: 'RPE 7 — algo duro: hablar ya cuesta.',
    cardioHelpRpeTip: 'En resumen: ritmo controlado pero exigente. Si puedes charlar tranquilo, vas suave; si no dices ni una frase, te pasaste.',
    cardioHelpInt: 'Intervalos (p.ej. "4x4 min, 2 min descanso")',
    cardioHelpIntDesc: '"4x4" = 4 bloques de 4 minutos de trabajo a esa intensidad, con 2 minutos de recuperación suave (trote o andar) entre bloque y bloque.',
    cardioHelpIntExample: '[4 min fuerte] – 2 min suave – [4 min fuerte] – 2 min suave – [4 min fuerte] – 2 min suave – [4 min fuerte]',
    pendingWarn: (n) => `Aún tienes sesiones pendientes en la Semana ${n}. Puedes registrar igualmente, pero te recomendamos completarlas antes para seguir la progresión.`,
    swimMeters: 'Metros nadados', cardioDistance: 'Distancia (m)', swimRpe: 'Esfuerzo',
    swimMetersPh: 'Lo que puedas',
    lastTimeSwim: 'Última vez', swimTarget: 'Objetivo',
    cardioTime: 'Tiempo', cardioTimePh: 'mm:ss', cardioPace: 'Ritmo', recordGps: 'Grabar con GPS',
    extraRunsTitle: 'Otras carreras del día', extraRunRemove: 'Eliminar',
    addManualRun: 'Añadir carrera a mano', cancel: 'Cancelar',
    paceRun: '/km', paceSwim: '/100m', lastPace: 'ritmo',
    swimFirstTime: 'Primera vez: nada a tu RPE y registra los metros que hagas. La próxima vez te mostraré tu marca y un objetivo.',
    cardioFirstTime: 'Primera vez: ve a tu RPE y registra la distancia que hagas. La próxima vez te mostraré tu marca y un objetivo.',
    setMarkDone: 'Marcar serie como hecha (inicia el descanso)', setDone: 'Serie hecha',
    promptTitle: '¿Damos el día por completado?',
    promptBody: 'Has marcado todas las series de este día.',
    promptYes: 'Sí, completar día', promptNo: 'Ahora no',
    lockedNote: 'Día completado. Desmárcalo si necesitas corregir algo.',
    scheduledOn: 'Programado', doneOn: 'Hecho',
    doneDateLabel: 'Fecha en la que lo hice',
    strengthHelpTitle: '¿Qué significa esto?',
    shSets: '4×8-10 → series × repeticiones',
    shSetsDesc: 'Haz 4 series de entre 8 y 10 repeticiones cada una. Empieza por el número bajo (8) y ve subiendo semana a semana hasta 10; cuando llegues arriba, sube el peso.',
    shRpe: '@RPE8 → esfuerzo percibido',
    shRpeDesc: 'Del 1 al 10, lo cerca que acabas del fallo. RPE 8 = podrías haber hecho 2 repeticiones más. RPE 10 = no podías ni una más. Elige el peso para acabar con esa sensación.',
    shRest: '90s descanso',
    shRestDesc: 'Segundos de descanso entre serie y serie. Púlsalo con el ✓ de cada serie y el temporizador arranca solo.',
    shTempo: '2-0-1 → tempo (segundos)',
    shTempoDesc: 'El ritmo de cada repetición, en segundos y en este orden: bajada — pausa abajo — subida.',
    shTempoExample: '2-0-1 = bajas en 2 segundos, sin pausa abajo, y subes en 1 segundo (explosivo). Bajar controlado es lo que más músculo construye.',
  },
  en: {
    back: 'Back to plans', loading: 'Loading...', week: 'Week', deload: 'Deload',
    rest: 'rest', save: 'Save', saved: 'Saved', saving: 'Saving...', saveError: 'Save error',
    done: 'Completed', markDone: 'Mark as completed', notes: 'Session notes', notesPh: 'How it felt, loads, etc.',
    timerLabel: 'Session time', timerStart: 'Start', timerStop: 'Finish', timerReset: 'Reset',
    set: 'Set', reps: 'Reps', weight: 'Weight (kg)', target: 'Target', progression: 'Progression',
    addSet: 'Add set', removeSet: 'Remove set',
    cardio: 'Cardio', intensity: 'Intensity', intervals: 'Intervals', duration: 'Duration',
    restTimer: 'Rest', mistakes: 'Common mistakes', instructions: 'Instructions', viewDetail: 'View detail',
    noDetail: 'No further detail for this exercise.', resume: 'Resume', pause: 'Pause', reset: 'Reset',
    lastTime: 'Last time’s value (edit if it changes)',
    swap: 'Swap', swapTitle: 'Swap exercise', swapHint: 'Tap an exercise to see its detail, or “Swap” to replace it. Only changes this session: sets, reps and rest are kept.',
    addExercise: 'Add exercise', addTitle: 'Add exercise', addHint: 'Search and tap an exercise to add it to this day (3×8-12 by default; adjust it afterwards).', add: 'Add',
    searchPh: 'Search exercise...', noResults: 'No results', current: 'Current',
    swapToThis: 'Swap for this one',
    suggest: 'Suggest sets',
    rmTitle: 'Your strength (1RM)', rmCurrent: 'Your 1RM', rmNone: 'You haven’t logged your 1RM for this exercise yet.',
    rmKnown: 'I know it', rmEstimate: 'Estimate from a set', rmSave: 'Save', rmCalc: 'Calculate & save',
    rmKg: '1RM (kg)', rmManual: 'manual', rmEstimated: 'estimated',
    rmHint: 'Used to propose weights with "Suggest sets".',
    rmSetWeight: 'Weight (kg)', rmSetReps: 'Reps', rmExample: 'E.g.: 50 kg × 8 reps → estimates your 1RM',
    rmAddedWeight: 'Added weight (kg)',
    rmBodyweightHint: 'This is a bodyweight exercise: put 0 if you do it with just your body, or the kg you add (vest, backpack, plate). I work out the real load from your bodyweight.',
    cardioHelpTitle: 'What does this mean?',
    cardioHelpRpe: 'RPE = Perceived Exertion',
    cardioHelpRpeDesc: 'A 1-to-10 scale of how hard YOU feel the effort (no heart-rate monitor needed). 1 = standing still, 10 = maximal.',
    cardioHelpRpe6: 'RPE 6 — moderate: you can speak short sentences.',
    cardioHelpRpe7: 'RPE 7 — somewhat hard: talking gets difficult.',
    cardioHelpRpeTip: 'In short: controlled but demanding. If you can chat easily you’re too easy; if you can’t say a word, too hard.',
    cardioHelpInt: 'Intervals (e.g. "4x4 min, 2 min rest")',
    cardioHelpIntDesc: '"4x4" = 4 blocks of 4 minutes of work at that intensity, with 2 minutes of easy recovery (jog or walk) between blocks.',
    cardioHelpIntExample: '[4 min hard] – 2 min easy – [4 min hard] – 2 min easy – [4 min hard] – 2 min easy – [4 min hard]',
    pendingWarn: (n) => `You still have pending sessions in Week ${n}. You can log anyway, but we recommend completing them first to follow the progression.`,
    swimMeters: 'Meters swum', cardioDistance: 'Distance (m)', swimRpe: 'Effort',
    swimMetersPh: 'As far as you can',
    lastTimeSwim: 'Last time', swimTarget: 'Target',
    cardioTime: 'Time', cardioTimePh: 'mm:ss', cardioPace: 'Pace', recordGps: 'Record with GPS',
    extraRunsTitle: 'Other runs of the day', extraRunRemove: 'Remove',
    addManualRun: 'Add run manually', cancel: 'Cancel',
    paceRun: '/km', paceSwim: '/100m', lastPace: 'pace',
    swimFirstTime: 'First time: swim at your RPE and log the meters you do. Next time I’ll show your mark and a target.',
    cardioFirstTime: 'First time: go at your RPE and log the distance you cover. Next time I’ll show your mark and a target.',
    setMarkDone: 'Mark set as done (starts the rest)', setDone: 'Set done',
    promptTitle: 'Mark the day as done?',
    promptBody: 'You’ve marked every set for this day.',
    promptYes: 'Yes, complete day', promptNo: 'Not yet',
    lockedNote: 'Day completed. Unmark it if you need to correct something.',
    scheduledOn: 'Scheduled', doneOn: 'Done',
    doneDateLabel: 'Date I actually did it',
    strengthHelpTitle: 'What does this mean?',
    shSets: '4×8-10 → sets × reps',
    shSetsDesc: 'Do 4 sets of 8 to 10 reps each. Start at the low end (8) and build up week by week to 10; once you reach the top, increase the weight.',
    shRpe: '@RPE8 → perceived effort',
    shRpeDesc: 'From 1 to 10, how close to failure you finish. RPE 8 = you could have done 2 more reps. RPE 10 = not a single one more. Pick the weight that leaves you there.',
    shRest: '90s rest',
    shRestDesc: 'Seconds of rest between sets. Tap the ✓ on each set and the timer starts by itself.',
    shTempo: '2-0-1 → tempo (seconds)',
    shTempoDesc: 'The pace of each rep, in seconds, in this order: lowering — pause at the bottom — lifting.',
    shTempoExample: '2-0-1 = lower in 2 seconds, no pause at the bottom, lift in 1 second (explosive). Lowering under control is what builds the most muscle.',
  },
};

// --- Cardio time & pace helpers -------------------------------------------------------
// Time is entered and shown as mm:ss but stored as whole seconds, so it survives a round trip
// through JSON without float drift.
function parseTime(str) {
  if (str === null || str === undefined || str === '') return null;
  const s = String(str).trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10) * 60;      // a bare number means minutes
  const m = s.match(/^(\d+):([0-5]?\d)$/);               // mm:ss
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}
function fmtTime(sec) {
  if (sec === null || sec === undefined || sec === '' || isNaN(sec)) return '';
  const s = Math.round(Number(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
// Session stopwatch display: h:mm:ss once past an hour, otherwise m:ss.
function fmtDuration(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
    : `${m}:${String(ss).padStart(2, '0')}`;
}
// The phone's numeric keypad has no colon key, so the time field takes plain digits and inserts
// the colon itself: the rightmost two digits are seconds. Typing 2630 shows 26:30.
function fmtDigits(digits) {
  const d = String(digits || '').replace(/\D/g, '').slice(0, 5);
  return d.length <= 2 ? d : `${d.slice(0, -2)}:${d.slice(-2)}`;
}
function digitsToSec(digits) {
  const d = String(digits || '').replace(/\D/g, '').slice(0, 5);
  if (!d) return null;
  return parseInt(d.slice(0, -2) || '0', 10) * 60 + parseInt(d.slice(-2) || '0', 10);
}
// Pace is derived, never stored: one source of truth (time & distance) can't contradict itself.
// Running -> min/km, swimming -> min/100m.
function paceSeconds(timeSec, distanceM, swim) {
  const t = Number(timeSec), d = Number(distanceM);
  if (!t || !d || t <= 0 || d <= 0) return null;
  return t / (d / (swim ? 100 : 1000));
}

// The AI sometimes returns cardio intensity/intervals as objects or arrays; never render those raw
function asText(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (Array.isArray(v)) return v.map(asText).filter(Boolean).join(' · ');
  if (typeof v === 'object') {
    const parts = [];
    if (v.repeat) parts.push(`${v.repeat}x`);
    if (v.duration_min) parts.push(`${v.duration_min} min`);
    ['intensity', 'zone', 'type', 'effort', 'pace', 'notes'].forEach(k => { if (v[k]) parts.push(String(v[k])); });
    return parts.length ? parts.join(' ') : Object.values(v).map(asText).filter(Boolean).join(' ');
  }
  return String(v);
}

// Render text that may contain numbered steps ("1. ... 2. ...") one per line
function NumberedText({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(?=\d+\.\s)/).map(s => s.trim()).filter(Boolean);
  if (parts.length <= 1) return <p className="text-sm text-stone font-inter whitespace-pre-line">{text}</p>;
  return (
    <div className="space-y-1">
      {parts.map((p, i) => <p key={i} className="text-sm text-stone font-inter">{p}</p>)}
    </div>
  );
}

export default function TrainingPlanDetailPage() {
  const { language } = useI18n();
  const navigate = useNavigate();
  const { planId } = useParams();
  const t = L[language] || L.es;

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openWeek, setOpenWeek] = useState(0);
  const [openDay, setOpenDay] = useState(null); // session_id of the currently expanded day
  const [linkedNutritionId, setLinkedNutritionId] = useState(null);

  // Autosave bookkeeping
  const [saveStatus, setSaveStatus] = useState({}); // session_id -> 'saving' | 'saved' | 'error'
  const saveTimers = useRef({});
  const pendingRef = useRef({});
  const planRef = useRef(null);

  // Exercise detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailEx, setDetailEx] = useState(null);

  // Strength (1RM) + set suggestions
  const [strengthMap, setStrengthMap] = useState({}); // exercise_id -> { one_rm, source }
  const [rmManual, setRmManual] = useState('');
  const [rmWeight, setRmWeight] = useState('');
  const [rmReps, setRmReps] = useState('');
  const [savingRm, setSavingRm] = useState(false);
  const [suggestingId, setSuggestingId] = useState(null);

  // Raw text of each cardio time box, keyed by session. Needed because "28:" is a valid thing
  // to be typing but not a parseable time — controlling the box by the parsed value would erase
  // it mid-keystroke. The draft holds what the user sees; time_sec holds what we store.
  const [timeDraft, setTimeDraft] = useState({});
  const [manualRun, setManualRun] = useState(null); // { sessionId, dist, digits } for the add-run-by-hand form
  const [nowTs, setNowTs] = useState(() => Date.now()); // 1 s tick that drives the live session stopwatch

  // "All sets ticked -> close the day?" prompt. Dismissing it must not nag again.
  const [completePrompt, setCompletePrompt] = useState(null); // session_id | null
  const dismissedPrompt = useRef({});

  // Swap exercise
  const [swap, setSwap] = useState(null); // { sessionId, ei, ex }
  const [swapCandidate, setSwapCandidate] = useState(null); // library exercise being previewed from the swap list
  const [swapSearch, setSwapSearch] = useState('');
  const [swapResults, setSwapResults] = useState([]);
  const [swapLoading, setSwapLoading] = useState(false);
  const swapSearchTimer = useRef(null);

  // Cardio help
  const [cardioHelpOpen, setCardioHelpOpen] = useState(false);
  const [strengthHelpOpen, setStrengthHelpOpen] = useState(false);

  // Rest timer
  const [timer, setTimer] = useState(null); // { total, remaining, label, running }

  useEffect(() => { planRef.current = plan; });

  useEffect(() => {
    fetchPlan();
    fetchLinkedNutrition();
    fetchStrengths();
    return () => { Object.values(saveTimers.current).forEach(clearTimeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  // Countdown loop (depends only on running so the interval isn't recreated every second)
  useEffect(() => {
    if (!timer?.running) return;
    const id = setInterval(() => {
      setTimer(tm => {
        if (!tm || !tm.running) return tm;
        const r = tm.remaining - 1;
        if (r <= 0) { playBeep(); try { navigator.vibrate?.([300, 120, 300]); } catch { } return { ...tm, remaining: 0, running: false }; }
        return { ...tm, remaining: r };
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timer?.running]);

  const fetchLinkedNutrition = async () => {
    try {
      const res = await fetch(`${API}/nutrition/plans`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        const linked = (d.plans || []).find(p => p.training_plan_id === planId);
        setLinkedNutritionId(linked ? linked.plan_id : null);
      }
    } catch (e) { console.error(e); }
  };

  const fetchPlan = async () => {
    try {
      const res = await fetch(`${API}/training/plans/${planId}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPlan(data);
        // Open the week you're actually up to, not always week 1: the first week that still
        // has a pending session. If every week is done, fall back to the last one.
        const weeks = data.week_list || [];
        let wi = weeks.findIndex(wk => (wk.sessions || []).some(s => !s.completed));
        if (wi < 0) wi = Math.max(0, weeks.length - 1);
        setOpenWeek(wi);
        const wk = weeks[wi];
        if (wk) {
          const pending = (wk.sessions || []).find(s => !s.completed) || (wk.sessions || [])[0];
          setOpenDay(pending ? pending.session_id : null);
        }
      } else { toast.error('Error'); navigate('/training/plans'); }
    } catch { navigate('/training/plans'); }
    finally { setLoading(false); }
  };

  // ---------- Load memory (prefill reps/weight from last time) ----------
  // Returns the logged sets of an exercise as [{reps, weight}], or null if nothing logged
  const cleanedLogs = (ex) => {
    const logs = ex.logs || [];
    const n = ex.sets || logs.length;
    const out = [];
    let any = false;
    for (let i = 0; i < n; i++) {
      const l = logs[i] || {};
      const reps = (l.reps !== undefined && l.reps !== null && l.reps !== '') ? l.reps : '';
      const weight = (l.weight !== undefined && l.weight !== null && l.weight !== '') ? l.weight : '';
      if (reps !== '' || weight !== '') any = true;
      out.push({ reps, weight });
    }
    return any ? out : null;
  };

  // prefillMap[session_id][exerciseIndex] = [{reps, weight}, ...] coming from the most
  // recent EARLIER session (chronological) that has the same exercise_id logged.
  const prefillMap = useMemo(() => {
    const map = {};
    if (!plan?.week_list) return map;
    const flat = [];
    plan.week_list.forEach((wk, wi) => (wk.sessions || []).forEach((s, si) => flat.push({ s, wi, si })));
    flat.sort((a, b) => {
      const da = a.s.date || '', db = b.s.date || '';
      if (da !== db) return da < db ? -1 : 1;
      if (a.wi !== b.wi) return a.wi - b.wi;
      return a.si - b.si;
    });
    const lastByEx = {};
    for (const { s } of flat) {
      const perSession = {};
      (s.exercises || []).forEach((ex, ei) => {
        if (ex.exercise_id && lastByEx[ex.exercise_id]) perSession[ei] = lastByEx[ex.exercise_id];
      });
      map[s.session_id] = perSession;
      // Update history AFTER recording suggestions, so a session never suggests from itself
      (s.exercises || []).forEach((ex) => {
        const cleaned = cleanedLogs(ex);
        if (ex.exercise_id && cleaned) lastByEx[ex.exercise_id] = cleaned;
      });
    }
    return map;
  }, [plan]);
  const prefillRef = useRef({});
  prefillRef.current = prefillMap;

  // Cardio memory: cardioPrefill[session_id] = { distance_m, date } from the most recent
  // EARLIER session with the same cardio type that has a logged distance.
  const cardioPrefill = useMemo(() => {
    const map = {};
    if (!plan?.week_list) return map;
    const flat = [];
    plan.week_list.forEach((wk, wi) => (wk.sessions || []).forEach((s, si) => flat.push({ s, wi, si })));
    flat.sort((a, b) => {
      const da = a.s.date || '', db = b.s.date || '';
      if (da !== db) return da < db ? -1 : 1;
      if (a.wi !== b.wi) return a.wi - b.wi;
      return a.si - b.si;
    });
    const lastByType = {};
    for (const { s } of flat) {
      const c = s.cardio;
      if (!c) continue;
      const type = String(c.type || 'cardio').toLowerCase();
      map[s.session_id] = lastByType[type] ? { ...lastByType[type] } : null;
      const dist = c.log && c.log.distance_m;
      if (dist !== undefined && dist !== null && dist !== '') {
        const ts = c.log && c.log.time_sec;
        lastByType[type] = {
          distance_m: Number(dist), date: s.date,
          time_sec: (ts !== undefined && ts !== null && ts !== '') ? Number(ts) : null,
        };
      }
    }
    return map;
  }, [plan]);
  const cardioPrefillRef = useRef({});
  cardioPrefillRef.current = cardioPrefill;

  // Fill empty set slots of a session with the suggested (last-time) values so they
  // become real logged values. Used when the user actually interacts with a session.
  const hydrateSession = (session) => {
    const sug = prefillRef.current[session.session_id];
    let exercises = session.exercises || [];
    if (sug) {
      exercises = exercises.map((ex, ei) => {
        const exSug = sug[ei];
        if (!exSug) return ex;
        const logs = [...(ex.logs || [])];
        for (let i = 0; i < (ex.sets || 0); i++) {
          const cur = logs[i] || {};
          const hasReps = cur.reps !== undefined && cur.reps !== null && cur.reps !== '';
          const hasWeight = cur.weight !== undefined && cur.weight !== null && cur.weight !== '';
          const s = exSug[i];
          if (s && (!hasReps || !hasWeight)) {
            logs[i] = { set: i + 1, reps: hasReps ? cur.reps : (s.reps ?? ''), weight: hasWeight ? cur.weight : (s.weight ?? '') };
          }
        }
        return { ...ex, logs };
      });
    }
    // Cardio distance is NOT auto-filled from last time: the suggestion shows as a placeholder,
    // and recording a distance the user didn't actually cover would be wrong.
    return { ...session, exercises, cardio: session.cardio };
  };

  // ---------- Autosave ----------
  const getSession = (sessionId) => {
    const p = planRef.current;
    if (!p) return null;
    for (const w of p.week_list) for (const s of w.sessions) if (s.session_id === sessionId) return s;
    return null;
  };

  const doSaveObj = async (session) => {
    if (!session) return;
    const id = session.session_id;
    setSaveStatus(prev => ({ ...prev, [id]: 'saving' }));
    try {
      const res = await fetch(`${API}/training/plans/${planId}/sessions/${id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(session),
      });
      setSaveStatus(prev => ({ ...prev, [id]: res.ok ? 'saved' : 'error' }));
    } catch {
      setSaveStatus(prev => ({ ...prev, [id]: 'error' }));
    }
  };

  const scheduleSave = (session) => {
    const id = session.session_id;
    pendingRef.current[id] = session;
    clearTimeout(saveTimers.current[id]);
    setSaveStatus(prev => ({ ...prev, [id]: 'saving' }));
    saveTimers.current[id] = setTimeout(() => doSaveObj(pendingRef.current[id]), 800);
  };

  const replaceSession = (sessionId, newSession, immediate) => {
    setPlan(prev => ({
      ...prev,
      week_list: prev.week_list.map(w => ({
        ...w,
        sessions: w.sessions.map(s => (s.session_id === sessionId ? newSession : s)),
      })),
    }));
    planRef.current = {
      ...planRef.current,
      week_list: planRef.current.week_list.map(w => ({
        ...w,
        sessions: w.sessions.map(s => (s.session_id === sessionId ? newSession : s)),
      })),
    };
    if (immediate) { clearTimeout(saveTimers.current[sessionId]); doSaveObj(newSession); }
    else scheduleSave(newSession);
  };

  const setLog = (sessionId, ei, setIdx, field, value) => {
    const cur = getSession(sessionId);
    if (!cur) return;
    // Update ONLY the field the user touched. We deliberately do NOT hydrate (materialize the
    // suggested last-time values) here: doing so on every keystroke made a cleared field refill
    // itself the moment you edited any other field. Suggestions stay as placeholders until the
    // user applies them ("Sugerir series") or completes the day.
    const exercises = (cur.exercises || []).map((e, idx) => (idx === ei ? { ...e, logs: [...(e.logs || [])] } : e));
    const ex = exercises[ei];
    while (ex.logs.length <= setIdx) ex.logs.push({ set: ex.logs.length + 1, reps: '', weight: '' });
    ex.logs[setIdx] = { ...ex.logs[setIdx], set: setIdx + 1, [field]: value };
    replaceSession(sessionId, { ...cur, exercises }, false);
  };

  // Add or remove a set for a single exercise in this session (min 1). Removing drops the
  // trailing log so the count and the logged sets stay in sync; the AI later sees the real
  // number of sets the user did that day.
  const changeSetCount = (sessionId, ei, delta) => {
    const cur = getSession(sessionId);
    if (!cur) return;
    const exercises = (cur.exercises || []).map((e, idx) => {
      if (idx !== ei) return e;
      const n = Math.max(1, (parseInt(e.sets) || 0) + delta);
      const logs = [...(e.logs || [])].slice(0, n);
      return { ...e, sets: n, logs };
    });
    replaceSession(sessionId, { ...cur, exercises }, true);
  };

  // Every prescribed set of every exercise has been ticked off.
  // Only meaningful for strength work: a cardio-only day has no sets, so it would be
  // vacuously "all done" — callers must guard against that.
  const allSetsDone = (sess) => {
    const exs = sess?.exercises || [];
    if (exs.length === 0) return false;
    return exs.every(ex => {
      const n = parseInt(ex.sets) || 0;
      if (n <= 0) return true;
      const logs = ex.logs || [];
      return Array.from({ length: n }).every((_, i) => !!(logs[i] || {}).done);
    });
  };

  // Mark/unmark a single set as done; marking it starts the rest timer automatically
  const toggleSetDone = (sessionId, ei, setIdx, restSec, exName) => {
    const cur = getSession(sessionId);
    if (!cur) return;
    // Don't hydrate here either — ticking a set must not overwrite fields the user cleared.
    // Untouched fields are still committed from the suggestions when the day is completed.
    const exercises = (cur.exercises || []).map((e, idx) => (idx === ei ? { ...e, logs: [...(e.logs || [])] } : e));
    const ex = exercises[ei];
    while (ex.logs.length <= setIdx) ex.logs.push({ set: ex.logs.length + 1, reps: '', weight: '' });
    const wasDone = !!ex.logs[setIdx].done;
    ex.logs[setIdx] = { ...ex.logs[setIdx], set: setIdx + 1, done: !wasDone };
    const next = { ...cur, exercises };
    replaceSession(sessionId, next, false);
    if (!wasDone) {
      startTimer(restSec || 90, exName); // starting this set's rest
      // Ticking the last remaining set: offer to close the day (once per session).
      if (!next.completed && !dismissedPrompt.current[sessionId] && allSetsDone(next)) {
        setCompletePrompt(sessionId);
      }
    }
  };

  const toggleDone = (sessionId) => {
    const cur = getSession(sessionId);
    if (!cur) return;
    const completed = !cur.completed;
    // When marking done, persist the suggested values that weren't manually changed
    const base = completed ? hydrateSession(cur) : cur;
    replaceSession(sessionId, { ...base, completed, completed_at: completed ? new Date().toISOString() : null }, true);
  };

  // The day you actually trained, which may differ from the scheduled date.
  const doneDate = (sess) => (sess?.completed_at ? String(sess.completed_at).slice(0, 10) : null);

  // Midday local keeps the ISO date stable once sliced back to YYYY-MM-DD.
  const setDoneDate = (sessionId, value) => {
    const cur = getSession(sessionId);
    if (!cur || !value) return;
    replaceSession(sessionId, { ...cur, completed_at: new Date(`${value}T12:00:00`).toISOString() }, true);
  };

  const confirmCompletePrompt = () => {
    const id = completePrompt;
    setCompletePrompt(null);
    if (id) toggleDone(id);
  };

  const dismissCompletePrompt = () => {
    if (completePrompt) dismissedPrompt.current[completePrompt] = true;
    setCompletePrompt(null);
  };

  const setNotes = (sessionId, value) => {
    const cur = getSession(sessionId);
    if (!cur) return;
    replaceSession(sessionId, { ...cur, session_notes: value }, false);
  };

  const setCardioLog = (sessionId, field, value) => {
    const cur = getSession(sessionId);
    if (!cur || !cur.cardio) return;
    const cardio = { ...cur.cardio, log: { ...(cur.cardio.log || {}), [field]: value } };
    replaceSession(sessionId, { ...cur, cardio }, false);
  };

  // Remove one of the day's extra GPS runs (the main run lives in cardio.log, not here).
  const removeExtraRun = (sessionId, idx) => {
    const cur = getSession(sessionId);
    if (!cur || !cur.cardio) return;
    const extras = [...(cur.cardio.extra_runs || [])];
    extras.splice(idx, 1);
    const cardio = { ...cur.cardio, extra_runs: extras };
    replaceSession(sessionId, { ...cur, cardio }, true);
  };

  // ---------- Session stopwatch (per day) ----------
  // Wall-clock based: we store the start timestamp, so the count survives locking the phone or
  // leaving the page, and accumulates across start/stop pauses into duration_sec.
  const startSessionTimer = (sessionId) => {
    const cur = getSession(sessionId);
    if (!cur || cur.timer_started_at) return;
    replaceSession(sessionId, { ...cur, timer_started_at: new Date().toISOString() }, true);
    setNowTs(Date.now());
  };
  const stopSessionTimer = (sessionId) => {
    const cur = getSession(sessionId);
    if (!cur || !cur.timer_started_at) return;
    const add = Math.max(0, (Date.now() - new Date(cur.timer_started_at).getTime()) / 1000);
    replaceSession(sessionId, { ...cur, duration_sec: Math.round((cur.duration_sec || 0) + add), timer_started_at: null }, true);
  };
  const resetSessionTimer = (sessionId) => {
    const cur = getSession(sessionId);
    if (!cur) return;
    replaceSession(sessionId, { ...cur, duration_sec: 0, timer_started_at: null }, true);
  };
  // Seconds to show for a session: saved duration plus the live stretch if it's running.
  const sessionTimerSec = (sess) => (sess.duration_sec || 0) +
    (sess.timer_started_at ? Math.max(0, (nowTs - new Date(sess.timer_started_at).getTime()) / 1000) : 0);

  // Tick every second only while some day's stopwatch is running.
  const anyTimerRunning = !!plan && (plan.week_list || []).some(w => (w.sessions || []).some(s => s.timer_started_at));
  useEffect(() => {
    if (!anyTimerRunning) return;
    const iv = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [anyTimerRunning]);

  // Add a run by hand (no live GPS) — e.g. to log a run recorded elsewhere. Follows the same
  // rule as the GPS tracker: if the main run of the day is empty it fills that, otherwise it's
  // stored as an extra run.
  const addManualRun = (sessionId, distanceM, timeSec) => {
    const cur = getSession(sessionId);
    if (!cur || !cur.cardio || !distanceM) return;
    const swim = isSwim(cur.cardio);
    const pace = paceSeconds(timeSec, distanceM, swim);
    const hasMain = cur.cardio.log && cur.cardio.log.distance_m != null && cur.cardio.log.distance_m !== '';
    let cardio;
    if (!hasMain) {
      cardio = { ...cur.cardio, log: { ...(cur.cardio.log || {}), distance_m: distanceM, time_sec: timeSec } };
    } else {
      const entry = {
        distance_m: distanceM, time_sec: timeSec,
        pace_sec: pace ? Math.round(pace) : null, manual: true,
        recorded_at: new Date().toISOString(),
      };
      cardio = { ...cur.cardio, extra_runs: [...(cur.cardio.extra_runs || []), entry] };
    }
    replaceSession(sessionId, { ...cur, cardio }, true);
  };

  // ---------- Exercise detail ----------
  const openDetail = async (exerciseId, candidate = null) => {
    if (!exerciseId) return;
    setSwapCandidate(candidate); // only set when previewing from the swap list
    setDetailEx(null);
    setRmManual(''); setRmWeight(''); setRmReps('');
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await fetch(`${API}/exercises/${exerciseId}`, { credentials: 'include' });
      if (res.ok) setDetailEx(await res.json());
    } catch { /* ignore */ }
    finally { setDetailLoading(false); }
  };

  // ---------- Swap exercise (this session only) ----------
  const loadSwapResults = async (muscle, search) => {
    setSwapLoading(true);
    try {
      const params = new URLSearchParams();
      if (search && search.trim()) params.set('search', search.trim());
      else if (muscle) params.set('muscle', muscle);
      params.set('limit', '60');
      const res = await fetch(`${API}/exercises?${params.toString()}`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setSwapResults(d.exercises || []); }
      else setSwapResults([]);
    } catch { setSwapResults([]); }
    finally { setSwapLoading(false); }
  };

  const openSwap = (sessionId, ei, ex) => {
    setSwap({ sessionId, ei, ex, mode: 'swap' });
    setSwapSearch('');
    setSwapResults([]);
    loadSwapResults((ex.muscle_groups || [])[0] || '', '');
  };

  // Add a brand-new exercise to a session. Reuses the swap picker in 'add' mode.
  const openAddExercise = (sessionId) => {
    setSwap({ sessionId, ei: null, ex: null, mode: 'add' });
    setSwapSearch('');
    setSwapResults([]);
    loadSwapResults('', '');
  };

  const onSwapSearchChange = (val) => {
    setSwapSearch(val);
    clearTimeout(swapSearchTimer.current);
    const muscle = (swap?.ex?.muscle_groups || [])[0] || '';
    swapSearchTimer.current = setTimeout(() => loadSwapResults(muscle, val), 350);
  };

  const applySwap = (lib) => {
    if (!swap) return;
    const { sessionId, ei, mode } = swap;
    const cur = getSession(sessionId);
    if (!cur) return;
    const name = (language === 'es' ? lib.name_es : lib.name_en) || lib.name_es || lib.name_en;

    let exercises;
    if (mode === 'add') {
      // A fresh exercise with sensible defaults (same shape the plan generator produces).
      const newEx = {
        exercise_id: lib.exercise_id, name,
        muscle_groups: lib.muscle_groups || [], image_url: lib.image_url || null, video_url: lib.video_url || null,
        sets: 3, reps: '8-12', rest_sec: 90, rpe: 7, tempo: '', notes: '', logs: [],
      };
      exercises = [...(cur.exercises || []), newEx];
    } else {
      exercises = (cur.exercises || []).map((e, idx) => (idx === ei ? {
        ...e, exercise_id: lib.exercise_id, name,
        muscle_groups: lib.muscle_groups || [], image_url: lib.image_url || null, video_url: lib.video_url || null,
        logs: [], // different exercise → previous logs no longer apply
      } : e));
    }
    replaceSession(sessionId, { ...cur, exercises }, true);
    setSwap(null);
    setSwapCandidate(null);
    setDetailOpen(false);
    toast.success(mode === 'add'
      ? (language === 'es' ? 'Ejercicio añadido' : 'Exercise added')
      : (language === 'es' ? 'Ejercicio cambiado' : 'Exercise swapped'));
  };

  // ---------- Strength (1RM) & suggestions ----------
  const fetchStrengths = async () => {
    try {
      const res = await fetch(`${API}/training/strength`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); setStrengthMap(d.strengths || {}); }
    } catch { /* ignore */ }
  };

  const saveStrength = async (exerciseId, payload) => {
    setSavingRm(true);
    try {
      const res = await fetch(`${API}/training/strength/${exerciseId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const rec = await res.json();
        setStrengthMap(prev => ({ ...prev, [exerciseId]: rec }));
        setRmManual(''); setRmWeight(''); setRmReps('');
        toast.success(language === 'es' ? `1RM guardado: ${rec.one_rm} kg` : `1RM saved: ${rec.one_rm} kg`);
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || (language === 'es' ? 'Error' : 'Error'));
      }
    } catch { toast.error(language === 'es' ? 'Error' : 'Error'); }
    finally { setSavingRm(false); }
  };

  const suggestSession = async (sessionId) => {
    const cur = getSession(sessionId);
    if (!cur) return;
    setSuggestingId(sessionId);
    try {
      const res = await fetch(`${API}/training/plans/${planId}/sessions/${sessionId}/suggest`, {
        method: 'POST', credentials: 'include',
      });
      if (!res.ok) { toast.error(language === 'es' ? 'Error' : 'Error'); return; }
      const d = await res.json();
      const byId = {};
      (d.suggestions || []).forEach(s => { byId[s.exercise_id] = s; });
      const exercises = (cur.exercises || []).map(ex => {
        const s = byId[ex.exercise_id];
        if (!s) return ex;
        const logs = [...(ex.logs || [])];
        for (let i = 0; i < (ex.sets || 0); i++) {
          const c = logs[i] || {};
          const hasReps = c.reps !== undefined && c.reps !== null && c.reps !== '';
          const hasWeight = c.weight !== undefined && c.weight !== null && c.weight !== '';
          logs[i] = {
            set: i + 1,
            reps: hasReps ? c.reps : (s.reps ?? ''),
            weight: hasWeight ? c.weight : (s.weight !== null && s.weight !== undefined ? s.weight : ''),
          };
        }
        return { ...ex, logs };
      });
      replaceSession(sessionId, { ...cur, exercises }, true);
      const sm = d.summary || {};
      toast.success(language === 'es'
        ? `Sugerencias aplicadas: ${sm.rm || 0} por RM, ${sm.ai || 0} estimadas por IA${sm.none ? `, ${sm.none} sin peso` : ''}`
        : `Suggestions applied: ${sm.rm || 0} by RM, ${sm.ai || 0} AI-estimated${sm.none ? `, ${sm.none} without weight` : ''}`);
      // Tell the user WHY some had no weight, so it doesn't look broken
      if (sm.none) {
        if (d.ai_status === 'unavailable') {
          toast.error(language === 'es'
            ? 'No se pudo conectar con la IA para estimar pesos. Registra tu 1RM o reintenta. (Si es en local, puede ser tu antivirus/proxy interceptando HTTPS.)'
            : 'Could not reach the AI to estimate weights. Log your 1RM or retry. (Locally this may be antivirus/proxy intercepting HTTPS.)');
        } else if (d.ai_status === 'skipped') {
          toast.message(language === 'es'
            ? 'Completa tu perfil de entrenamiento para que la IA estime los pesos de los accesorios.'
            : 'Complete your training profile so the AI can estimate accessory weights.');
        }
      }
    } catch { toast.error(language === 'es' ? 'Error' : 'Error'); }
    finally { setSuggestingId(null); }
  };

  // ---------- Rest timer ----------
  const playBeep = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      [0, 0.25, 0.5].forEach(offset => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = 'sine';
        o.frequency.value = 880;
        const start = ctx.currentTime + offset;
        g.gain.setValueAtTime(0.0001, start);
        g.gain.exponentialRampToValueAtTime(0.3, start + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, start + 0.18);
        o.start(start);
        o.stop(start + 0.2);
      });
    } catch { /* ignore */ }
  };

  const startTimer = (seconds, label) => {
    const s = Math.max(5, parseInt(seconds) || 90);
    setTimer({ total: s, remaining: s, label: label || t.restTimer, running: true });
  };
  const adjustTimer = (delta) => setTimer(tm => (tm ? { ...tm, remaining: Math.max(0, tm.remaining + delta), running: tm.remaining + delta > 0 ? tm.running : false } : tm));
  const toggleTimer = () => setTimer(tm => (tm ? { ...tm, running: !tm.running && tm.remaining > 0 ? true : false } : tm));
  const resetTimer = () => setTimer(tm => (tm ? { ...tm, remaining: tm.total, running: true } : tm));
  const closeTimer = () => setTimer(null);
  const fmtClock = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // Bodyweight exercise: the "weight" you log is ADDED weight (0 = just your body)
  const isBodyweightEx = (ex) => {
    const eq = (ex?.equipment || []).map(e => String(e).toLowerCase());
    return eq.length > 0 && eq.every(e => e === 'body weight');
  };

  const isSwim = (cardio) => {
    const txt = String(cardio?.type || '').toLowerCase();
    return ['swim', 'nataci', 'piscina', 'pool'].some(k => txt.includes(k));
  };

  // First earlier week (by index) that still has incomplete sessions, or -1
  const firstIncompleteEarlierWeek = (weekIndex) => {
    const wl = plan?.week_list || [];
    for (let i = 0; i < weekIndex; i++) {
      if ((wl[i].sessions || []).some(s => !s.completed)) return i;
    }
    return -1;
  };

  const SaveBadge = ({ status }) => {
    if (!status) return null;
    if (status === 'saving') return <span className="text-xs text-stone flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />{t.saving}</span>;
    if (status === 'saved') return <span className="text-xs text-sage flex items-center gap-1"><Check className="w-3 h-3" />{t.saved}</span>;
    return <span className="text-xs text-destructive">{t.saveError}</span>;
  };

  if (loading) return <Layout><div className="max-w-4xl mx-auto px-4 py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  if (!plan) return null;

  const detailImg = detailEx?.image_hd || detailEx?.image_url;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 pb-32">
        <button onClick={() => navigate('/training/plans')} className="flex items-center gap-2 text-stone hover:text-primary mb-4 font-inter">
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </button>
        <h1 className="font-manrope font-bold text-3xl text-charcoal mb-1">{plan.name}</h1>
        {plan.progression_strategy && (
          <p className="text-sm text-stone font-inter mb-3"><b>{t.progression}:</b> {plan.progression_strategy}</p>
        )}
        <Button onClick={() => navigate(linkedNutritionId ? `/nutrition/plans/${linkedNutritionId}` : `/nutrition/plans?training_plan_id=${planId}`)} className="rounded-full bg-primary mb-6">
          <Utensils className="w-4 h-4 mr-2" />
          {linkedNutritionId
            ? (language === 'es' ? 'Ver nutrición de este plan' : "View this plan's nutrition")
            : (language === 'es' ? 'Generar nutrición para este plan' : 'Generate nutrition for this plan')}
        </Button>

        <div className="space-y-3">
          {plan.week_list.map((wk, wi) => {
            const open = openWeek === wi;
            const doneCount = wk.sessions.filter(s => s.completed).length;
            return (
              <Card key={wi} className="bg-white rounded-2xl shadow-soft overflow-hidden">
                <button
                  onClick={() => {
                    if (open) { setOpenWeek(-1); }
                    else {
                      setOpenWeek(wi);
                      const pending = wk.sessions.find(s => !s.completed) || wk.sessions[0];
                      setOpenDay(pending ? pending.session_id : null);
                    }
                  }}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className={`font-manrope font-bold ${wk.is_deload ? 'text-sage' : 'text-charcoal'}`}>
                      {t.week} {wk.week_index + 1}{wk.is_deload ? ` · ${t.deload}` : ''}
                    </span>
                    <span className="text-xs text-stone">{doneCount}/{wk.sessions.length} ✓</span>
                  </div>
                  {open ? <ChevronUp className="w-5 h-5 text-stone" /> : <ChevronDown className="w-5 h-5 text-stone" />}
                </button>

                {open && (
                  <div className="px-3 pb-3 space-y-2">
                    {wk.sessions.map((sess, si) => {
                      const dayOpen = openDay === sess.session_id;
                      // A completed day is read-only: unmark it to edit again.
                      const locked = !!sess.completed;
                      const dDone = doneDate(sess);
                      const movedDay = dDone && dDone !== sess.date;
                      return (
                        <div key={sess.session_id} className={`border rounded-2xl overflow-hidden ${sess.completed ? 'border-sage/40 bg-sage/5' : 'border-border'}`}>
                          {/* Day header (collapsible) */}
                          <button
                            onClick={() => setOpenDay(dayOpen ? null : sess.session_id)}
                            className="w-full flex items-center justify-between gap-2 p-4 text-left"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              {sess.completed && <Check className="w-4 h-4 text-sage shrink-0" />}
                              <div className="min-w-0">
                                <p className="font-manrope font-semibold text-charcoal truncate">{sess.day_label}</p>
                                <p className="text-xs text-stone truncate">
                                  {movedDay
                                    ? <>{t.scheduledOn}: <span className="line-through">{sess.date}</span> · <b className="text-sage">{t.doneOn}: {dDone}</b></>
                                    : sess.date}
                                  {' · '}{sess.focus}{sess.duration_min ? ` · ${sess.duration_min} min` : ''}{sess.duration_sec ? ` · ⏱ ${fmtDuration(sess.duration_sec)}` : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <SaveBadge status={saveStatus[sess.session_id]} />
                              {dayOpen ? <ChevronUp className="w-5 h-5 text-stone" /> : <ChevronDown className="w-5 h-5 text-stone" />}
                            </div>
                          </button>

                          {dayOpen && (
                            <div className="px-4 pb-4">
                              {(() => {
                                const warnWeek = firstIncompleteEarlierWeek(wi);
                                if (warnWeek < 0) return null;
                                return (
                                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 mb-3 text-sm">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>{t.pendingWarn(warnWeek + 1)}</span>
                                  </div>
                                );
                              })()}
                              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                                {(sess.exercises || []).length > 0 && !locked ? (
                                  <button onClick={() => suggestSession(sess.session_id)} disabled={suggestingId === sess.session_id} className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border-2 border-primary text-primary hover:bg-primary/5 disabled:opacity-60">
                                    {suggestingId === sess.session_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {t.suggest}
                                  </button>
                                ) : <span />}
                                <button onClick={() => toggleDone(sess.session_id)} className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border-2 ${sess.completed ? 'bg-sage text-white border-sage' : 'border-border text-stone'}`}>
                                  <Check className="w-4 h-4" /> {sess.completed ? t.done : t.markDone}
                                </button>
                              </div>

                              {/* Session stopwatch: time the whole workout; saved and shown per day */}
                              {(() => {
                                const running = !!sess.timer_started_at;
                                const secs = sessionTimerSec(sess);
                                const has = secs > 0 || running;
                                if (locked && !has) return null; // completed day never timed → nothing to show
                                return (
                                  <div className="flex items-center justify-between gap-3 bg-cream/60 border border-border rounded-xl p-3 mb-3">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <Timer className={`w-5 h-5 shrink-0 ${running ? 'text-primary animate-pulse' : 'text-stone'}`} />
                                      <div className="min-w-0">
                                        <p className="text-[11px] text-stone leading-none mb-1">{t.timerLabel}</p>
                                        <p className="font-manrope font-extrabold text-xl tabular-nums text-charcoal leading-none">{fmtDuration(secs)}</p>
                                      </div>
                                    </div>
                                    {!locked && (
                                      <div className="flex items-center gap-2 shrink-0">
                                        {has && !running && (
                                          <button type="button" onClick={() => resetSessionTimer(sess.session_id)} title={t.timerReset}
                                            className="text-stone hover:text-red-500 p-1.5"><RotateCcw className="w-4 h-4" /></button>
                                        )}
                                        {running ? (
                                          <button type="button" onClick={() => stopSessionTimer(sess.session_id)}
                                            className="flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold bg-red-600 text-white hover:bg-red-700">
                                            <Square className="w-4 h-4" /> {t.timerStop}
                                          </button>
                                        ) : (
                                          <button type="button" onClick={() => startSessionTimer(sess.session_id)}
                                            className="flex items-center gap-1 px-4 py-2 rounded-full text-sm font-semibold bg-primary text-white hover:bg-primary/90">
                                            <Play className="w-4 h-4" /> {t.timerStart}
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}

                              {locked && (
                                <div className="flex flex-wrap items-center justify-between gap-3 bg-sage/10 border border-sage/30 text-charcoal rounded-xl p-3 mb-3">
                                  <span className="flex items-center gap-2 text-sm text-stone">
                                    <Lock className="w-4 h-4 text-sage shrink-0" />{t.lockedNote}
                                  </span>
                                  <label className="flex items-center gap-2 text-xs text-stone">
                                    {t.doneDateLabel}
                                    <Input
                                      type="date"
                                      value={dDone || ''}
                                      max={new Date().toISOString().slice(0, 10)}
                                      onChange={(e) => setDoneDate(sess.session_id, e.target.value)}
                                      className="h-9 w-40 rounded-lg text-sm bg-white"
                                    />
                                  </label>
                                </div>
                              )}

                              {/* Cardio */}
                              {sess.cardio && (
                                <div className="bg-muted/50 rounded-xl p-3 mb-3">
                                  <div className="flex items-start gap-2">
                                    <Flame className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                                    <div className="text-sm flex-1 min-w-0">
                                      <p className="font-semibold text-charcoal capitalize">
                                        {[asText(sess.cardio.type), sess.cardio.duration_min ? `${sess.cardio.duration_min} min` : '']
                                          .filter(Boolean).join(' · ') || t.cardio}
                                      </p>
                                      <p className="text-stone">{t.intensity}: {asText(sess.cardio.intensity) || '—'} · {t.intervals}: {asText(sess.cardio.intervals) || '—'}</p>
                                      {sess.cardio.notes && <p className="text-stone italic">{asText(sess.cardio.notes)}</p>}
                                    </div>
                                    <button type="button" onClick={() => setCardioHelpOpen(true)} title={t.cardioHelpTitle} className="shrink-0 text-stone hover:text-primary">
                                      <Info className="w-4 h-4" />
                                    </button>
                                  </div>

                                  {/* Cardio logging + memory/progression */}
                                  {(() => {
                                    const swim = isSwim(sess.cardio);
                                    const pf = cardioPrefill[sess.session_id];
                                    const logged = sess.cardio.log?.distance_m;
                                    const hasLog = logged !== undefined && logged !== null && logged !== '';
                                    const suggested = pf ? Math.round((pf.distance_m * (wk.is_deload ? 0.8 : 1.03)) / 50) * 50 : null;

                                    const timeSec = sess.cardio.log?.time_sec;
                                    const hasTime = timeSec !== undefined && timeSec !== null && timeSec !== '';
                                    // Live pace only from what's actually been entered — no phantom pace off a suggestion.
                                    const curPace = paceSeconds(hasTime ? timeSec : null, hasLog ? logged : null, swim);
                                    const lastPace = pf ? paceSeconds(pf.time_sec, pf.distance_m, swim) : null;
                                    const paceUnit = swim ? t.paceSwim : t.paceRun;

                                    return (
                                      <div className="mt-3 pt-3 border-t border-border/50 space-y-2">
                                        {/* GPS recording: only in the app, and not for pool swimming. Fills the
                                            fields below automatically when you finish the run. */}
                                        {isNativeApp && !swim && !locked && (
                                          <Button onClick={() => navigate(`/track?plan=${planId}&session=${sess.session_id}`)}
                                            className="w-full rounded-full bg-primary h-11 mb-1">
                                            <Satellite className="w-4 h-4 mr-2" /> {t.recordGps}
                                          </Button>
                                        )}
                                        <div className="flex items-end gap-2 flex-wrap">
                                          <div className="flex-1 min-w-[110px]">
                                            <label className="block text-[11px] text-stone mb-1">{swim ? t.swimMeters : t.cardioDistance}</label>
                                            <div className="relative">
                                              {/* Value is only what the user actually typed; the last-time distance is a
                                                  placeholder, so it can be cleared and edited freely (it used to snap back). */}
                                              <Input type="number" min="0" step="50" inputMode="numeric" value={hasLog ? logged : ''}
                                                disabled={locked}
                                                onChange={(e) => setCardioLog(sess.session_id, 'distance_m', e.target.value)}
                                                placeholder={pf ? String(pf.distance_m) : t.swimMetersPh}
                                                className="h-10 rounded-lg text-sm pr-8" />
                                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone text-xs">m</span>
                                            </div>
                                          </div>
                                          <div className="w-24">
                                            <label className="block text-[11px] text-stone mb-1">{t.cardioTime}</label>
                                            {/* Digits in, mm:ss out — the colon is auto-inserted so it works with the
                                                phone's numeric keypad (which has no ':' key). Stored as seconds. */}
                                            <Input type="text" inputMode="numeric" disabled={locked}
                                              value={timeDraft[sess.session_id] != null ? fmtDigits(timeDraft[sess.session_id]) : (hasTime ? fmtTime(timeSec) : '')}
                                              onChange={(e) => {
                                                const digits = e.target.value.replace(/\D/g, '').slice(0, 5);
                                                setTimeDraft(d => ({ ...d, [sess.session_id]: digits }));
                                                setCardioLog(sess.session_id, 'time_sec', digitsToSec(digits));
                                              }}
                                              placeholder={t.cardioTimePh} className="h-10 rounded-lg text-sm" />
                                          </div>
                                          <div className="w-20">
                                            <label className="block text-[11px] text-stone mb-1">{t.swimRpe}</label>
                                            <Input type="number" min="1" max="10" step="0.5" inputMode="decimal"
                                              disabled={locked}
                                              value={sess.cardio.log?.rpe ?? ''} onChange={(e) => setCardioLog(sess.session_id, 'rpe', e.target.value)}
                                              placeholder="RPE" className="h-10 rounded-lg text-sm" />
                                          </div>
                                        </div>

                                        {curPace && (
                                          <p className="text-xs text-charcoal">
                                            {t.cardioPace}: <b className="text-primary">{fmtTime(curPace)} {paceUnit}</b>
                                          </p>
                                        )}

                                        {pf ? (
                                          <p className="text-xs text-stone">
                                            {t.lastTimeSwim}: <b>{pf.distance_m} m</b>
                                            {lastPace ? <> ({fmtTime(lastPace)} {paceUnit})</> : null}
                                            {' · '}{t.swimTarget}: <b className="text-primary">{suggested} m</b>
                                            {wk.is_deload ? ` (${t.deload.toLowerCase()})` : ''}
                                          </p>
                                        ) : (
                                          <p className="text-xs text-stone italic">{swim ? t.swimFirstTime : t.cardioFirstTime}</p>
                                        )}

                                        {/* Extra GPS runs recorded the same day (the main one lives above) */}
                                        {(sess.cardio.extra_runs || []).length > 0 && (
                                          <div className="pt-2 mt-1 border-t border-border/50">
                                            <p className="text-[11px] font-semibold text-stone mb-1">{t.extraRunsTitle}</p>
                                            <div className="space-y-1">
                                              {sess.cardio.extra_runs.map((r, ri) => {
                                                const km = r.distance_m >= 1000 ? `${(r.distance_m / 1000).toFixed(2)} km` : `${Math.round(r.distance_m)} m`;
                                                const pc = r.pace_sec || paceSeconds(r.time_sec, r.distance_m, swim);
                                                return (
                                                  <div key={ri} className="flex items-center gap-2 text-xs text-charcoal">
                                                    <b className="tabular-nums">{km}</b>
                                                    {r.time_sec != null && <span className="text-stone tabular-nums">· {fmtTime(r.time_sec)}</span>}
                                                    {pc ? <span className="text-primary tabular-nums">· {fmtTime(pc)} {paceUnit}</span> : null}
                                                    {typeof r.elevation_m === 'number' && r.elevation_m > 0 && <span className="text-stone">· ↑{r.elevation_m} m</span>}
                                                    {!locked && (
                                                      <button type="button" onClick={() => removeExtraRun(sess.session_id, ri)} title={t.extraRunRemove}
                                                        className="ml-auto text-stone hover:text-red-500 shrink-0">
                                                        <X className="w-3.5 h-3.5" />
                                                      </button>
                                                    )}
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}

                                        {/* Add a run by hand (no live GPS) */}
                                        {!locked && (
                                          manualRun?.sessionId === sess.session_id ? (
                                            <div className="pt-2 mt-1 border-t border-border/50 space-y-2">
                                              <p className="text-[11px] font-semibold text-stone">{t.addManualRun}</p>
                                              <div className="flex items-end gap-2">
                                                <div className="flex-1 min-w-0">
                                                  <label className="block text-[11px] text-stone mb-1">{swim ? t.swimMeters : t.cardioDistance}</label>
                                                  <Input type="number" min="0" inputMode="numeric" value={manualRun.dist}
                                                    onChange={(e) => setManualRun(m => ({ ...m, dist: e.target.value }))}
                                                    className="h-10 rounded-lg text-sm" />
                                                </div>
                                                <div className="w-24">
                                                  <label className="block text-[11px] text-stone mb-1">{t.cardioTime}</label>
                                                  <Input type="text" inputMode="numeric" value={fmtDigits(manualRun.digits || '')}
                                                    onChange={(e) => setManualRun(m => ({ ...m, digits: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                                                    placeholder={t.cardioTimePh} className="h-10 rounded-lg text-sm" />
                                                </div>
                                              </div>
                                              <div className="flex gap-2">
                                                <Button size="sm"
                                                  onClick={() => {
                                                    const d = parseInt(manualRun.dist) || 0;
                                                    if (d > 0) addManualRun(sess.session_id, d, digitsToSec(manualRun.digits || ''));
                                                    setManualRun(null);
                                                  }}
                                                  className="rounded-full bg-primary h-9 flex-1">{t.save}</Button>
                                                <Button size="sm" variant="ghost" onClick={() => setManualRun(null)} className="rounded-full h-9">{t.cancel}</Button>
                                              </div>
                                            </div>
                                          ) : (
                                            <button type="button" onClick={() => setManualRun({ sessionId: sess.session_id, dist: '', digits: '' })}
                                              className="flex items-center gap-1 text-xs text-primary hover:underline mt-1">
                                              <Plus className="w-3.5 h-3.5" /> {t.addManualRun}
                                            </button>
                                          )
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}

                              {/* Strength exercises */}
                              {(sess.exercises || []).map((ex, ei) => (
                                <div key={ei} className="border-t border-border/60 py-3">
                                  <div className="flex items-center gap-3 mb-2">
                                    <button
                                      type="button"
                                      onClick={() => openDetail(ex.exercise_id)}
                                      className="relative w-16 h-16 rounded-xl overflow-hidden bg-muted shrink-0 group"
                                      title={t.viewDetail}
                                    >
                                      {ex.image_url
                                        ? <img src={ex.image_url} alt={ex.name} className="w-full h-full object-cover" loading="lazy" />
                                        : <div className="w-full h-full flex items-center justify-center"><Dumbbell className="w-6 h-6 text-stone" /></div>}
                                      <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                                        <Info className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                      </span>
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <button type="button" onClick={() => openDetail(ex.exercise_id)} className="text-left min-w-0">
                                          <p className="font-manrope font-semibold text-charcoal text-sm hover:text-primary transition-colors truncate">{ex.name}</p>
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => startTimer(ex.rest_sec || 90, ex.name)}
                                          title={t.restTimer}
                                          className="shrink-0 text-stone hover:text-primary"
                                        >
                                          <Timer className="w-4 h-4" />
                                        </button>
                                      </div>
                                      <p className="text-xs text-stone flex items-center gap-1 flex-wrap">
                                        <span>{t.target}: {ex.sets}×{ex.reps} @RPE{ex.rpe} · {ex.rest_sec}s {t.rest}{ex.tempo ? ` · ${ex.tempo}` : ''}</span>
                                        <button type="button" onClick={() => setStrengthHelpOpen(true)} title={t.strengthHelpTitle} className="text-stone hover:text-primary shrink-0">
                                          <Info className="w-3.5 h-3.5" />
                                        </button>
                                      </p>
                                    </div>
                                    {!locked && (
                                      <button
                                        type="button"
                                        onClick={() => openSwap(sess.session_id, ei, ex)}
                                        title={t.swap}
                                        className="shrink-0 flex items-center gap-1 text-xs text-stone hover:text-primary border border-border hover:border-primary/40 rounded-full px-2.5 py-1.5"
                                      >
                                        <Repeat className="w-3.5 h-3.5" /><span className="hidden sm:inline">{t.swap}</span>
                                      </button>
                                    )}
                                  </div>
                                  {/* Log grid */}
                                  <div className="space-y-1">
                                    {/* Column headers so it's clear which field is which */}
                                    <div className="flex items-center gap-2 pb-0.5">
                                      <span className="w-12" />
                                      <span className="flex-1 min-w-0 text-[11px] font-semibold text-stone">{t.reps}</span>
                                      <span className="flex-1 min-w-0 text-[11px] font-semibold text-stone">{t.weight}</span>
                                      <span className="w-9 shrink-0" />
                                    </div>
                                    {Array.from({ length: ex.sets }).map((_, setIdx) => {
                                      const lg = (ex.logs || [])[setIdx] || {};
                                      const sug = ((prefillMap[sess.session_id] || {})[ei] || [])[setIdx] || null;
                                      const repsLogged = lg.reps !== undefined && lg.reps !== null && lg.reps !== '';
                                      const weightLogged = lg.weight !== undefined && lg.weight !== null && lg.weight !== '';
                                      // The VALUE is only what the user actually logged; the last-time / AI suggestion
                                      // is shown as a PLACEHOLDER so the field can be cleared and stays clear.
                                      const repsVal = repsLogged ? lg.reps : '';
                                      const weightVal = weightLogged ? lg.weight : '';
                                      const repsPh = (sug && sug.reps !== '' && sug.reps != null) ? String(sug.reps) : t.reps;
                                      const weightPh = (sug && sug.weight !== '' && sug.weight != null) ? String(sug.weight) : t.weight;
                                      const setDone = !!lg.done;
                                      return (
                                        <div key={setIdx} className="flex items-center gap-2">
                                          <span className="text-xs text-stone w-12">{t.set} {setIdx + 1}</span>
                                          <Input type="number" min="0" inputMode="numeric" disabled={locked} value={repsVal} onChange={(e) => setLog(sess.session_id, ei, setIdx, 'reps', e.target.value)} placeholder={repsPh} title={repsPh !== t.reps ? t.lastTime : undefined} className="h-9 flex-1 min-w-0 rounded-lg text-sm" />
                                          <Input type="number" min="0" step="0.5" inputMode="decimal" disabled={locked} value={weightVal} onChange={(e) => setLog(sess.session_id, ei, setIdx, 'weight', e.target.value)} placeholder={weightPh} title={weightPh !== t.weight ? t.lastTime : undefined} className="h-9 flex-1 min-w-0 rounded-lg text-sm" />
                                          <button
                                            type="button"
                                            disabled={locked}
                                            onClick={() => toggleSetDone(sess.session_id, ei, setIdx, ex.rest_sec, ex.name)}
                                            title={setDone ? t.setDone : t.setMarkDone}
                                            className={`h-9 w-9 shrink-0 rounded-lg border flex items-center justify-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${setDone ? 'bg-sage border-sage text-white' : 'border-border text-stone hover:text-primary hover:border-primary/40'}`}
                                          >
                                            <Check className="w-4 h-4" />
                                          </button>
                                        </div>
                                      );
                                    })}
                                    {/* Add / remove a set for this exercise on this day (min 1) */}
                                    {!locked && (
                                      <div className="flex items-center gap-2 pt-1">
                                        <button
                                          type="button"
                                          onClick={() => changeSetCount(sess.session_id, ei, -1)}
                                          disabled={(parseInt(ex.sets) || 0) <= 1}
                                          className="flex items-center gap-1 text-xs text-stone hover:text-primary border border-border hover:border-primary/40 rounded-full px-2.5 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                          <Minus className="w-3.5 h-3.5" /> {t.removeSet}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => changeSetCount(sess.session_id, ei, 1)}
                                          className="flex items-center gap-1 text-xs text-stone hover:text-primary border border-border hover:border-primary/40 rounded-full px-2.5 py-1.5"
                                        >
                                          <Plus className="w-3.5 h-3.5" /> {t.addSet}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}

                              {!locked && (
                                <Button variant="outline" onClick={() => openAddExercise(sess.session_id)}
                                  className="w-full mt-3 rounded-xl border-dashed border-primary/40 text-primary h-11 hover:bg-primary/5">
                                  <Plus className="w-4 h-4 mr-2" /> {t.addExercise}
                                </Button>
                              )}

                              <Textarea value={sess.session_notes || ''} disabled={locked} onChange={(e) => setNotes(sess.session_id, e.target.value)} placeholder={t.notesPh} className="rounded-xl border-border bg-white/50 mt-3 text-sm" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      {/* All sets ticked -> offer to close the day */}
      <Dialog open={!!completePrompt} onOpenChange={(o) => { if (!o) dismissCompletePrompt(); }}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <Check className="w-5 h-5 text-sage" />{t.promptTitle}
            </DialogTitle>
            <DialogDescription className="font-inter">{t.promptBody}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end mt-2">
            <Button variant="ghost" onClick={dismissCompletePrompt} className="rounded-full">{t.promptNo}</Button>
            <Button onClick={confirmCompletePrompt} className="rounded-full bg-sage hover:bg-sage/90">
              <Check className="w-4 h-4 mr-1" />{t.promptYes}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exercise detail modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">{detailEx ? (language === 'es' ? detailEx.name_es : detailEx.name_en) : ''}</DialogTitle>
            <DialogDescription className="sr-only">{t.viewDetail}</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-12 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>
          ) : detailEx ? (
            <div className="space-y-4">
              {/* Came here from the swap list: allow swapping straight from the detail */}
              {swap && swapCandidate && swapCandidate.exercise_id === detailEx.exercise_id && swapCandidate.exercise_id !== swap.ex?.exercise_id && (
                <Button onClick={() => applySwap(swapCandidate)} className="rounded-full bg-primary w-full">
                  <Repeat className="w-4 h-4 mr-2" />{t.swapToThis}
                </Button>
              )}
              {detailEx.video_url ? (
                <video src={detailEx.video_url} controls playsInline poster={detailImg || undefined} className="w-full rounded-2xl bg-black aspect-video" />
              ) : detailImg ? (
                <img src={detailImg} alt={detailEx.name_es} className="w-full rounded-2xl object-cover bg-muted" />
              ) : null}

              {Array.isArray(detailEx.muscle_groups) && detailEx.muscle_groups.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {detailEx.muscle_groups.map((m, i) => (
                    <span key={i} className="text-xs bg-muted text-stone rounded-full px-2.5 py-1 capitalize">{m}</span>
                  ))}
                </div>
              )}

              {/* Strength (1RM) */}
              {detailEx.modality !== 'cardio' && (
                <div className="border border-border rounded-2xl p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="font-manrope font-semibold text-charcoal text-sm">{t.rmTitle}</p>
                    {strengthMap[detailEx.exercise_id]?.one_rm && (
                      <span className="text-sm text-primary font-semibold">
                        {t.rmCurrent}: {strengthMap[detailEx.exercise_id].one_rm} kg{' '}
                        <span className="text-xs text-stone font-normal">({strengthMap[detailEx.exercise_id].source === 'manual' ? t.rmManual : t.rmEstimated})</span>
                      </span>
                    )}
                  </div>
                  {!strengthMap[detailEx.exercise_id]?.one_rm && <p className="text-xs text-stone">{t.rmNone}</p>}
                  <p className="text-xs text-stone">{t.rmHint}</p>
                  {isBodyweightEx(detailEx) && <p className="text-xs text-primary">{t.rmBodyweightHint}</p>}

                  {/* Method 1: known 1RM */}
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-charcoal">{t.rmKnown}</p>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1 min-w-0">
                        <label className="block text-[11px] text-stone mb-1">{t.rmKg}</label>
                        <Input type="number" min="0" step="0.5" inputMode="decimal" value={rmManual} onChange={e => setRmManual(e.target.value)} placeholder="0" className="h-10 rounded-lg text-sm w-full" />
                      </div>
                      <Button size="sm" disabled={savingRm || !rmManual} onClick={() => saveStrength(detailEx.exercise_id, { one_rm: parseFloat(rmManual) })} className="rounded-full bg-primary shrink-0 h-10">
                        {savingRm ? <Loader2 className="w-4 h-4 animate-spin" /> : t.rmSave}
                      </Button>
                    </div>
                  </div>

                  {/* Method 2: estimate from a set */}
                  <div className="space-y-1.5 pt-1 border-t border-border/60">
                    <p className="text-xs font-semibold text-charcoal pt-2">{t.rmEstimate}</p>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1 min-w-0">
                        <label className="block text-[11px] text-stone mb-1">{isBodyweightEx(detailEx) ? t.rmAddedWeight : t.rmSetWeight}</label>
                        <Input type="number" min="0" step="0.5" inputMode="decimal" value={rmWeight} onChange={e => setRmWeight(e.target.value)} placeholder={isBodyweightEx(detailEx) ? '0' : '50'} className="h-10 rounded-lg text-sm w-full" />
                      </div>
                      <span className="text-stone pb-2">×</span>
                      <div className="flex-1 min-w-0">
                        <label className="block text-[11px] text-stone mb-1">{t.rmSetReps}</label>
                        <Input type="number" min="1" inputMode="numeric" value={rmReps} onChange={e => setRmReps(e.target.value)} placeholder="8" className="h-10 rounded-lg text-sm w-full" />
                      </div>
                      <Button size="sm" disabled={savingRm || !rmWeight || !rmReps} onClick={() => saveStrength(detailEx.exercise_id, { weight: parseFloat(rmWeight), reps: parseInt(rmReps) })} className="rounded-full bg-primary shrink-0 h-10">
                        {savingRm ? <Loader2 className="w-4 h-4 animate-spin" /> : t.rmCalc}
                      </Button>
                    </div>
                    <p className="text-[11px] text-stone">{t.rmExample}</p>
                  </div>
                </div>
              )}

              {(language === 'es' ? detailEx.instructions_es : detailEx.instructions_en) && (
                <div>
                  <p className="font-manrope font-semibold text-charcoal text-sm mb-1">{t.instructions}</p>
                  <NumberedText text={language === 'es' ? detailEx.instructions_es : detailEx.instructions_en} />
                </div>
              )}

              {(language === 'es' ? detailEx.common_mistakes_es : detailEx.common_mistakes_en) && (
                <div>
                  <p className="font-manrope font-semibold text-charcoal text-sm mb-1">{t.mistakes}</p>
                  <NumberedText text={language === 'es' ? detailEx.common_mistakes_es : detailEx.common_mistakes_en} />
                </div>
              )}

              {!detailEx.video_url && !detailImg && !detailEx.instructions_es && !detailEx.common_mistakes_es && (
                <p className="text-sm text-stone font-inter">{t.noDetail}</p>
              )}
            </div>
          ) : (
            <p className="text-sm text-stone font-inter py-6">{t.noDetail}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Swap exercise modal */}
      <Dialog open={!!swap} onOpenChange={(o) => { if (!o) setSwap(null); }}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[88vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-manrope">{swap?.mode === 'add' ? t.addTitle : t.swapTitle}</DialogTitle>
            <DialogDescription>{swap?.mode === 'add' ? t.addHint : t.swapHint}</DialogDescription>
          </DialogHeader>
          {swap && (
            <>
              {swap.mode !== 'add' && <p className="text-xs text-stone"><b>{t.current}:</b> {swap.ex?.name}</p>}
              <div className="relative">
                <Search className="w-4 h-4 text-stone absolute left-3 top-1/2 -translate-y-1/2" />
                <Input value={swapSearch} onChange={(e) => onSwapSearchChange(e.target.value)} placeholder={t.searchPh} className="h-11 rounded-xl pl-9" autoFocus />
              </div>
              <div className="overflow-y-auto -mx-2 px-2 space-y-1" style={{ maxHeight: '50vh' }}>
                {swapLoading ? (
                  <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                ) : swapResults.length === 0 ? (
                  <p className="text-sm text-stone text-center py-8">{t.noResults}</p>
                ) : (
                  swapResults.map((lib) => {
                    const isCurrent = lib.exercise_id === swap.ex?.exercise_id;
                    return (
                      <div
                        key={lib.exercise_id}
                        className={`w-full flex items-center gap-3 p-2 rounded-xl ${isCurrent ? 'opacity-50' : 'hover:bg-muted'}`}
                      >
                        {/* Tapping the exercise opens its detail; swapping needs the explicit button */}
                        <button
                          type="button"
                          onClick={() => openDetail(lib.exercise_id, lib)}
                          className="flex items-center gap-3 min-w-0 flex-1 text-left"
                          title={t.viewDetail}
                        >
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-muted shrink-0">
                            {lib.image_url
                              ? <img src={lib.image_url} alt={lib.name_es} className="w-full h-full object-cover" loading="lazy" />
                              : <div className="w-full h-full flex items-center justify-center"><Dumbbell className="w-5 h-5 text-stone" /></div>}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-manrope font-semibold text-charcoal text-sm truncate">{language === 'es' ? lib.name_es : lib.name_en}</p>
                            <p className="text-xs text-stone truncate">{(lib.muscle_groups || []).join(', ')}</p>
                          </div>
                        </button>
                        {isCurrent ? (
                          <span className="text-xs text-stone shrink-0 px-2">{t.current}</span>
                        ) : (
                          <Button size="sm" onClick={() => applySwap(lib)} className="rounded-full bg-primary shrink-0 h-8 text-xs">
                            {swap.mode === 'add' ? t.add : t.swap}
                          </Button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Strength help modal (sets, RPE, rest, tempo) */}
      <Dialog open={strengthHelpOpen} onOpenChange={setStrengthHelpOpen}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">{t.strengthHelpTitle}</DialogTitle>
            <DialogDescription className="sr-only">{t.strengthHelpTitle}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {[
              { k: t.shSets, d: t.shSetsDesc },
              { k: t.shRpe, d: t.shRpeDesc },
              { k: t.shRest, d: t.shRestDesc },
            ].map((row, i) => (
              <div key={i}>
                <p className="font-manrope font-semibold text-charcoal text-sm mb-1">{row.k}</p>
                <p className="text-sm text-stone font-inter">{row.d}</p>
              </div>
            ))}
            <div className="border-t border-border/60 pt-3">
              <p className="font-manrope font-semibold text-charcoal text-sm mb-1">{t.shTempo}</p>
              <p className="text-sm text-stone font-inter">{t.shTempoDesc}</p>
              <p className="text-xs text-stone font-inter mt-2 bg-muted/60 rounded-xl p-2">{t.shTempoExample}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cardio help modal */}
      <Dialog open={cardioHelpOpen} onOpenChange={setCardioHelpOpen}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">{t.cardioHelpTitle}</DialogTitle>
            <DialogDescription className="sr-only">{t.cardioHelpTitle}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="font-manrope font-semibold text-charcoal text-sm mb-1">{t.cardioHelpRpe}</p>
              <p className="text-sm text-stone font-inter">{t.cardioHelpRpeDesc}</p>
              <ul className="text-sm text-stone font-inter mt-2 space-y-1 list-disc pl-5">
                <li>{t.cardioHelpRpe6}</li>
                <li>{t.cardioHelpRpe7}</li>
              </ul>
              <p className="text-sm text-stone font-inter mt-2 italic">{t.cardioHelpRpeTip}</p>
            </div>
            <div className="border-t border-border/60 pt-3">
              <p className="font-manrope font-semibold text-charcoal text-sm mb-1">{t.cardioHelpInt}</p>
              <p className="text-sm text-stone font-inter">{t.cardioHelpIntDesc}</p>
              <p className="text-xs text-stone font-inter mt-2 bg-muted/60 rounded-xl p-2">{t.cardioHelpIntExample}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Centered rest timer (portaled to body so it isn't offset by transformed ancestors) */}
      {timer && createPortal(
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto bg-charcoal text-white rounded-3xl shadow-2xl px-6 py-6 w-full max-w-sm text-center animate-fade-in">
            <div className="flex items-center justify-center gap-2 mb-2 text-white/60 text-sm">
              <Timer className="w-4 h-4 text-primary shrink-0" />
              <span className="truncate">{timer.label}</span>
            </div>
            <p className={`font-manrope font-bold text-6xl tabular-nums mb-5 ${timer.remaining === 0 ? 'text-primary animate-pulse' : ''}`}>{fmtClock(timer.remaining)}</p>
            <div className="flex items-center justify-center gap-2">
              <button onClick={() => adjustTimer(-5)} title="-5s" className="h-12 px-4 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center gap-1 font-semibold"><Minus className="w-4 h-4" />5s</button>
              <button onClick={() => adjustTimer(5)} title="+5s" className="h-12 px-4 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center gap-1 font-semibold"><Plus className="w-4 h-4" />5s</button>
              {timer.remaining > 0 ? (
                <button onClick={toggleTimer} title={timer.running ? t.pause : t.resume} className="h-12 w-12 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center">
                  {timer.running ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </button>
              ) : (
                <button onClick={resetTimer} title={t.reset} className="h-12 w-12 rounded-2xl bg-primary hover:bg-primary/90 flex items-center justify-center"><RotateCcw className="w-5 h-5" /></button>
              )}
              <button onClick={closeTimer} title="Cerrar" className="h-12 w-12 rounded-2xl bg-white/10 hover:bg-white/20 flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </Layout>
  );
}
